import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../../components/loader/loader";
import BreadCrumpBack from "../../../components/navigator/back";
import Snackbar from "../../../components/snackbar/snackbar";
import Swal from "sweetalert2";
import { internshipRequestDetail, getInternshipRankings, selectInternshipApplicant, internshipRequestsList, evaluateInternshipProposal } from "../user.api";

// Helper to navigate in functional components
const navigateTo = (path) => {
  window.location.href = path;
};

export default function InternshipProposalDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [rankingsData, setRankingsData] = useState(null);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    fetchProposalDetail();
  }, [id]);

  useEffect(() => {
    if (activeTab === "applications" && id) {
      // Always refresh rankings data when switching to applications tab
      // This ensures data stays in sync with server state
      fetchRankingsData(true);
    }
  }, [activeTab, id]);

  const fetchRankingsData = (preserveManualSelections = false) => {
    if (!id) return;

    setRankingsLoading(true);
    console.log("Fetching rankings data for ID:", id);

    getInternshipRankings(id)
      .then((res) => {
        console.log("Rankings API Response:", res);

        if (res?.success && res?.data) {
          setRankingsData(res.data);
          
          // Always populate selected applicants based on API response and local state
          const selected = new Set();
          if (res.data.ranked_applicants) {
            res.data.ranked_applicants.forEach(applicant => {
              console.log("Applicant data:", applicant); // Debug log
              console.log("Proposal status:", applicant.proposal?.status); // Debug proposal status
              
              // Check if applicant is selected based on proposal status
              const isSelected = 
                applicant.proposal?.status === 'confirmed' ||
                applicant.proposal?.status === 'company_selected' ||
                applicant.proposal?.status === 'selected' ||
                applicant.proposal?.status === 'allotted' ||
                applicant.proposal?.status === 'doing' ||
                applicant.proposal?.status === 'evaluation' ||
                applicant.proposal?.status === 'evaluated';
              
              console.log("Is selected:", isSelected, "for status:", applicant.proposal?.status); // Debug selection logic
              
              if (isSelected) {
                selected.add(applicant.proposal?.id || applicant.application_id || applicant.internship.id);
                console.log("Found selected applicant:", applicant.proposal?.id || applicant.application_id || applicant.internship.id);
              }
            });
          }
          
          // Preserve manually selected applicants if flag is set
          if (preserveManualSelections) {
            selectedApplicants.forEach(id => selected.add(id));
          }
          
          setSelectedApplicants(selected);
          console.log("Selected applicants set:", Array.from(selected));
          
          console.log("Rankings data loaded successfully");
        } else {
          console.error(
            res?.data?.message || "Failed to fetch rankings data",
          );
          setRankingsData(null);
          // Don't clear selections if we're preserving manual selections
          if (!preserveManualSelections) {
            setSelectedApplicants(new Set());
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching rankings data:", err);
        setRankingsData(null);
        // Don't clear selections if we're preserving manual selections
        if (!preserveManualSelections) {
          setSelectedApplicants(new Set());
        }
      })
      .finally(() => {
        setRankingsLoading(false);
      });
  };

  const fetchProposalDetail = () => {
    if (!id) return;

    setLoading(true);
    console.log("Fetching proposal detail for ID:", id);

    internshipRequestDetail(id)
      .then((res) => {
        console.log("API Response:", res);

        if (res?.success && res?.data) {
          setData(res.data);
          console.log("Proposal details loaded successfully");
        } else {
          console.error(
            res?.data?.message || "Failed to fetch proposal details",
          );
          setData(null);
        }
      })
      .catch((err) => {
        console.error("Error fetching proposal details:", err);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditClick = () => {
    // Navigate to form with edit mode enabled
    navigateTo(`/internship-proposals-form?edit=${id}`);
  };

  const handleSelectApplicant = (applicant) => {
    const applicantId = applicant.proposal?.id || applicant.application_id || applicant.id;
    const proposalId = applicant.proposal?.id;
    
    // Check if applicant is already selected based on local state only
    if (selectedApplicants.has(applicantId)) {
      return; // Already selected, don't proceed
    }

    // Add to local state immediately for instant UI feedback
    setSelectedApplicants(prev => new Set(prev).add(applicantId));

    selectInternshipApplicant(proposalId)
      .then((res) => {
        if (res?.success) {
          setSnackMessage("Applicant selected successfully!");
          setIsSnackError(false);
          setSnackOpen(true);
          console.log("Applicant selected successfully");
          
          // Refresh rankings data to get updated status from server
          // Use preserveManualSelections to maintain the UI state during refresh
          setTimeout(() => {
            fetchRankingsData(true);
          }, 1000);
          
          // Also refresh the main listings data (this won't affect current UI)
          internshipRequestsList()
            .then((res) => {
              console.log("Internship listings refreshed successfully");
            })
            .catch((err) => {
              console.error("Error refreshing internship listings:", err);
            });
        } else {
          // Remove from local state if API call failed
          setSelectedApplicants(prev => {
            const newSet = new Set(prev);
            newSet.delete(applicantId);
            return newSet;
          });
          setSnackMessage(res?.message || "Failed to select applicant");
          setIsSnackError(true);
          setSnackOpen(true);
          console.error("Failed to select applicant");
        }
      })
      .catch((err) => {
        // Remove from local state if API call failed
        setSelectedApplicants(prev => {
          const newSet = new Set(prev);
          newSet.delete(applicantId);
          return newSet;
        });
        setSnackMessage("Error selecting applicant. Please try again.");
        setIsSnackError(true);
        setSnackOpen(true);
        console.error("Error selecting applicant:", err);
      });
  };

  const handleEvaluateApplicant = (proposalId) => {
    if (!proposalId) {
      setSnackMessage("Proposal ID is missing");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    Swal.fire({
      title: "Evaluate Applicant",
      html: `
        <div style="text-align: left;">
          <div class="mb-3 text-sm">
            <label for="evaluate-date" class="form-label">Completion Date</label>
            <input type="date" id="evaluate-date" class="form-control" required>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-remarks" class="form-label">Evaluation Remarks</label>
            <textarea id="evaluate-remarks" class="form-control" rows="3" required></textarea>
          </div>
          <div class="mb-3 text-sm">
            <label for="evaluate-file" class="form-label">Evaluation Report File</label>
            <input type="file" id="evaluate-file" class="form-control" accept=".pdf,.doc,.docx" required>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      confirmButtonText: "Evaluate",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const date = document.getElementById('evaluate-date').value;
        const remarks = document.getElementById('evaluate-remarks').value;
        const file = document.getElementById('evaluate-file').files[0];

        if (!date || !remarks || !file) {
          Swal.showValidationMessage('Please fill all fields and select a file');
          return false;
        }

        return { date, remarks, file };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { date, remarks, file } = result.value;
        
        // Create FormData
        const formData = new FormData();
        formData.append('completion_date', date);
        formData.append('evaluation_remarks', remarks);
        formData.append('evaluation_report_file', file);

        setIsUpdating(true);
        
        // Call the API with FormData
        evaluateInternshipProposal(proposalId, formData)
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res?.message || "Applicant evaluated successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              // Refresh the rankings data to get updated status
              fetchRankingsData(true);
            } else {
              setSnackMessage(res?.message || "Failed to evaluate applicant");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage(error?.message || "Error evaluating applicant");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  if (loading) return <Loader open={true} />;

  if (!data)
    return (
      <div className="container mt-5">
        <BreadCrumpBack />
        <div className="alert alert-warning mt-3">
          <p>
            No data found. The proposal may not exist or you don't have
            permission to view it.
          </p>
          <p className="text-muted small">
            Check the browser console for error details.
          </p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => window.history.back()}
          >
            Go Back
          </button>
        </div>
      </div>
    );

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString();
  };

  // Helper to get qualification names
  const getQualificationNames = (ids) => {
    if (!Array.isArray(ids) || !data?.all_qualifications) return "";

    // Convert object to array if needed (API returns object with string keys)
    const qualificationsArray = Array.isArray(data.all_qualifications)
      ? data.all_qualifications
      : Object.values(data.all_qualifications);

    return qualificationsArray
      .filter((q) => ids.includes(q.id))
      .map((q) => q.name)
      .join(", ");
  };

  // Helper to get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Helper to get application status badge color
  const getApplicationStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "confirmed":
        return "success";
      case "company_selected":
        return "info";
      case "selected":
        return "primary";
      case "allotted":
        return "warning";
      case "doing":
        return "info";
      case "evaluation":
        return "primary";
      case "evaluated":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Helper to format CGPA
  const formatCGPA = (cgpa) => {
    if (cgpa === null || cgpa === undefined) return "N/A";
    return cgpa.toFixed(1);
  };

  return (
    <div className="detail-page">
      <Loader open={isUpdating} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
      <div className="container mt-5 mb-5">
        <BreadCrumpBack />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mt-4">Internship Proposal Details</h3>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "details" ? "active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              Details
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "applications" ? "active" : ""}`}
              onClick={() => setActiveTab("applications")}
            >
              Application
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === "details" && (
          <div className="card p-4">
            <div className="d-flex justify-content-end mb-3">
              <button
                className="btn btn-primary btn-sm"
                onClick={handleEditClick}
              >
                Edit Proposal
              </button>
            </div>
            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Discipline:</div>
              <div className="col-md-9">{data.internship_discipline}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Brief Description:</div>
              <div className="col-md-9">{data.brief_description}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">
                Number of Positions:
              </div>
              <div className="col-md-9">{data.number_of_students}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Target Category:</div>
              <div className="col-md-9 text-capitalize">
                {data.target_category?.replace(/_/g, " ")}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Start Date:</div>
              <div className="col-md-9">
                {formatDate(data.preferred_start_date)}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">End Date:</div>
              <div className="col-md-9">
                {formatDate(data.preferred_end_date)}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">
                Additional Certifications:
              </div>
              <div className="col-md-9">
                {data.additional_certifications || "N/A"}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Grade Criteria:</div>
              <div className="col-md-9">{data.grade_criteria || "N/A"}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Status:</div>
              <div className="col-md-9">
                <span className={`badge bg-${getStatusBadgeClass(data.status)}`}>
                  {data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
                </span>
              </div>
            </div>

            <hr />

            <h5 className="mt-4 mb-3">Required Qualifications</h5>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">Qualification Type:</div>
              <div className="col-md-9">{data.qualification_type?.name}</div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">
              Qualifications:
              </div>
              <div className="col-md-9">
                {getQualificationNames(data.qualification_type_parents) || "N/A"}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3 font-weight-bold">
              Streams:
              </div>
              <div className="col-md-9">
                {getQualificationNames(data.qualification_type_subs) || "N/A"}
              </div>
            </div>
          </div>
        )}

        {activeTab === "applications" && (
          <div className="card p-4">
            {rankingsLoading ? (
              <Loader open={true} />
            ) : rankingsData?.ranked_applicants?.length > 0 ? (
              <>
                <div className="mb-4">
                  <h5 className="mb-3">Application Rankings</h5>
                  <div className="row mb-3">
                    <div className="col-md-3 font-weight-bold">Ranking Status:</div>
                    <div className="col-md-9">
                      <span className={`badge bg-${getApplicationStatusBadgeClass(rankingsData.ranking_status)}`}>
                        {rankingsData.ranking_status?.replace(/_/g, ' ').charAt(0).toUpperCase() + rankingsData.ranking_status?.replace(/_/g, ' ').slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '100px' }}>Effective Rank</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th style={{ width: '100px' }}>Phone</th>
                        <th style={{ width: '100px' }}>Latest CGPA</th>
                        <th style={{ width: '100px' }}>Latest Score</th>
                        <th style={{ width: '100px' }}>Preference Order</th>
                        <th>Status</th>
                        <th style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingsData.ranked_applicants.map((applicant, index) => (
                        <tr key={index}>
                          <td>
                            <span className="badge bg-primary">
                              {applicant.effective_rank}
                            </span>
                          </td>
                          <td>{applicant.internship.full_name}</td>
                          <td>{applicant.internship.email}</td>
                          <td>{applicant.internship.phone_no}</td>
                          <td>{formatCGPA(applicant.latest_cgpa)}</td>
                          <td>{applicant.latest_score || "N/A"}</td>
                          <td>{applicant.preference_order}</td>
                          <td>
                            <span className={`badge bg-${getApplicationStatusBadgeClass(applicant.proposal?.status?.toLowerCase() || 'pending')}`}>
                              {applicant.proposal?.status?.charAt(0).toUpperCase() + applicant.proposal?.status?.slice(1) || 'Pending'}
                            </span>
                          </td>
                          <td>
                            {applicant.proposal?.status === 'evaluation' ? (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleEvaluateApplicant(applicant.proposal?.id)}
                                disabled={isUpdating}
                              >
                                <i className="fa fa-check-circle"></i> Evaluate
                              </button>
                            ) : (
                              <button
                                className={`btn btn-sm ${selectedApplicants.has(applicant.proposal?.id || applicant.application_id || applicant.internship.id) ? 'btn-success' : 'btn-primary'}`}
                                onClick={() => handleSelectApplicant(applicant)}
                                disabled={selectedApplicants.has(applicant.proposal?.id || applicant.application_id || applicant.internship.id)}
                              >
                                {selectedApplicants.has(applicant.proposal?.id || applicant.application_id || applicant.internship.id) ? 'Selected' : 'Select'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="alert alert-info">
                No application data available for this internship proposal.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
